import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './ErrorPage.css'

type ErrorType = 'network' | 'device' | 'hls' | 'session' | 'timeout' | 'general'

interface ErrorConfig {
  icon: string
  title: string
  message: string
  suggestion: string
  retryable: boolean
}

const errorConfigs: Record<ErrorType, ErrorConfig> = {
  network: {
    icon: '🌐',
    title: 'Connection Issue',
    message: "We're sorry for the inconvenience. Please try again.",
    suggestion: 'Check your internet connection and try again.',
    retryable: true
  },
  device: {
    icon: '📱',
    title: 'Device Not Supported',
    message: 'Your device is not supported. This service requires iOS 14.0+ or Android 8.0+ with compatible browser.',
    suggestion: 'Please use a newer device or update your browser.',
    retryable: false
  },
  hls: {
    icon: '🎵',
    title: 'Audio Loading Failed',
    message: "We're sorry for the inconvenience. Please try again.",
    suggestion: 'The audio guide could not be loaded. Please check your connection.',
    retryable: true
  },
  session: {
    icon: '🔒',
    title: 'Session Expired',
    message: 'Your session has expired for security reasons.',
    suggestion: 'Please scan the QR code again to start a new session.',
    retryable: true
  },
  timeout: {
    icon: '⏱️',
    title: 'Loading Timeout',
    message: 'The service is taking longer than expected to load.',
    suggestion: 'Please check your connection and try again.',
    retryable: true
  },
  general: {
    icon: '⚠️',
    title: 'Service Temporarily Unavailable',
    message: 'The audio guide service is temporarily unavailable.',
    suggestion: 'Please try again in a few minutes.',
    retryable: true
  }
}

const ErrorPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [retryAttempts, setRetryAttempts] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)

  const errorType = (searchParams.get('type') as ErrorType) || 'general'
  const customMessage = searchParams.get('message')
  const config = errorConfigs[errorType]

  useEffect(() => {
    // Log error for analytics
    console.error('Error page displayed:', {
      type: errorType,
      customMessage,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    })

    // TODO: Send error analytics to Firebase
  }, [errorType, customMessage])

  const handleRetry = async () => {
    if (!config.retryable || isRetrying) return

    setIsRetrying(true)
    setRetryAttempts(prev => prev + 1)

    try {
      // Simulate retry delay
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Clear any cached errors
      localStorage.removeItem('audioGuideError')
      sessionStorage.removeItem('lastError')

      // Navigate back to appropriate page based on error type
      if (errorType === 'session') {
        navigate('/')
      } else {
        // Try to go back to the previous page, or home if none
        const previousPage = sessionStorage.getItem('previousPage')
        if (previousPage) {
          navigate(previousPage)
        } else {
          navigate('/')
        }
      }
    } catch (error) {
      console.error('Retry failed:', error)
      // Stay on error page if retry fails
    } finally {
      setIsRetrying(false)
    }
  }

  const handleGoHome = () => {
    // Clear all session data
    sessionStorage.clear()
    localStorage.removeItem('audioGuideProgress')
    localStorage.removeItem('audioGuideError')
    
    navigate('/')
  }

  const handleContactSupport = () => {
    // Create support email with error details
    const errorDetails = {
      errorType,
      customMessage,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      retryAttempts
    }

    const emailBody = `Hello HEP-FIVE Support,

I encountered an issue with the audio guide service:

Error Type: ${errorType}
Message: ${customMessage || config.message}
Time: ${errorDetails.timestamp}
Device: ${errorDetails.userAgent}
Retry Attempts: ${retryAttempts}

Please help me resolve this issue.

Thank you!`

    const emailUrl = `mailto:support@hep-five.jp?subject=Audio Guide Error Report&body=${encodeURIComponent(emailBody)}`
    window.open(emailUrl, '_blank')
  }

  return (
    <div className="error-page">
      {/* Background Container */}
      <div className="background-container">
        <img 
          src="/hep-five-primary-image.jpg" 
          alt="HEP-FIVE View"
          className="background-image"
        />
        <div className="background-overlay error-overlay"></div>
      </div>

      {/* Error Content */}
      <div className="error-content">
        <div className="error-message">
          <div className="error-icon">{config.icon}</div>
          
          <h1 className="error-title">{config.title}</h1>
          
          <p className="error-description">
            {customMessage || config.message}
          </p>
          
          <p className="error-suggestion">
            {config.suggestion}
          </p>

          {retryAttempts > 0 && (
            <div className="retry-info">
              <span>Retry attempts: {retryAttempts}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="error-actions">
          {config.retryable && (
            <button 
              className="retry-button"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  <span>Retrying...</span>
                </>
              ) : (
                <>
                  <span>🔄</span>
                  <span>Try Again</span>
                </>
              )}
            </button>
          )}

          <button 
            className="home-button"
            onClick={handleGoHome}
          >
            <span>🏠</span>
            <span>Go to Start</span>
          </button>

          <button 
            className="support-button"
            onClick={handleContactSupport}
          >
            <span>📧</span>
            <span>Contact Support</span>
          </button>
        </div>

        {/* Help Section */}
        <div className="help-section">
          <h3>Need Additional Help?</h3>
          <div className="help-items">
            <div className="help-item">
              <span className="help-icon">📶</span>
              <span>Check your internet connection</span>
            </div>
            <div className="help-item">
              <span className="help-icon">🔄</span>
              <span>Try refreshing the page</span>
            </div>
            <div className="help-item">
              <span className="help-icon">🎧</span>
              <span>Ensure headphones are connected</span>
            </div>
            <div className="help-item">
              <span className="help-icon">🔊</span>
              <span>Check your device volume</span>
            </div>
          </div>
        </div>

        {/* Credits */}
        <div className="error-credits">
          <div className="tech-credit">
            <img 
              src="/resense-streaming-logo.png" 
              alt="Re:Sense STREAMING" 
              className="resense-logo"
            />
          </div>
          
          <div className="company-credit">
            <span>Operated by Clepseadra, Inc.</span>
          </div>
        </div>

        {/* HEP-FIVE Logo */}
        <div className="logo-container">
          <img 
            src="/hep-five-logo.svg" 
            alt="HEP-FIVE" 
            className="hep-five-logo"
          />
        </div>
      </div>
    </div>
  )
}

export default ErrorPage